import * as Sentry from '@sentry/react';
import {
  type InfiniteData,
  type Query,
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import {
  type ApiRoutersPortfoliosPdpsPdpsImageViewFields,
  type SearchPdpImagesV1WorkspacesWorkspaceIdPdpsPdpIdImagesGetData,
  type SearchPdpImagesV1WorkspacesWorkspaceIdPdpsPdpIdImagesGetError,
  type SearchPdpImagesV1WorkspacesWorkspaceIdPdpsPdpIdImagesGetResponse,
  searchPdpImagesV1WorkspacesWorkspaceIdPdpsPdpIdImagesGet,
} from '../../generated';
import { extractTotalCount } from '../../utils';
import { PENDING_STALE_TIMEOUT_MS, getPendingImageTimestamps, pollWhilePending } from '../../utils/poll-while-pending';

export type SearchPdpImagesQueryVariables = SearchPdpImagesV1WorkspacesWorkspaceIdPdpsPdpIdImagesGetData['path'] &
  SearchPdpImagesV1WorkspacesWorkspaceIdPdpsPdpIdImagesGetData['query'];

export type SearchPdpImagesQuery = {
  data: SearchPdpImagesV1WorkspacesWorkspaceIdPdpsPdpIdImagesGetResponse;
  total_count: number;
};

export const useSearchPdpImagesQuery = <
  TData = SearchPdpImagesQuery,
  TError = SearchPdpImagesV1WorkspacesWorkspaceIdPdpsPdpIdImagesGetError,
>(
  {
    workspace_id,
    pdp_id,
    image_type,
    limit,
    offset,
    sort_by,
    sort_direction,
    source,
    exclude_source,
  }: SearchPdpImagesQueryVariables,
  options?: Omit<UseQueryOptions<SearchPdpImagesQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<SearchPdpImagesQuery, TError, TData>({
    queryKey: [
      'SearchPdpImages',
      { workspace_id, pdp_id, image_type, limit, offset, sort_by, sort_direction, source, exclude_source },
    ],
    queryFn: async () => {
      const { data, response } = await searchPdpImagesV1WorkspacesWorkspaceIdPdpsPdpIdImagesGet({
        path: { workspace_id, pdp_id },
        query: {
          image_type,
          limit,
          offset,
          sort_by: sort_by as ApiRoutersPortfoliosPdpsPdpsImageViewFields,
          sort_direction,
          source,
          exclude_source,
        },
        throwOnError: true,
      });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    refetchInterval: pollWhilePending(
      (query: Query<SearchPdpImagesQuery, TError>) => getPendingImageTimestamps(query.state.data?.data ?? []),
      {
        onTimeout: (pendingCount) => {
          Sentry.withScope((scope) => {
            scope.setFingerprint(['GET', '/v1/workspaces/{workspace_id}/pdps/{pdp_id}/images', 'polling_timeout']);

            Sentry.captureException(new Error('Images still pending after stale window for search pdp images query.'), {
              level: 'warning',
              tags: {
                feature: SENTRY_FEATURE_TAGS.Product,
                stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Product].productAssetsPolling,
              },
              extra: {
                workspace_id,
                pdp_id,
                pending_count: pendingCount,
                stale_window_ms: PENDING_STALE_TIMEOUT_MS,
                query_variables: {
                  workspace_id,
                  pdp_id,
                  image_type,
                  limit,
                  offset,
                  sort_by,
                  sort_direction,
                  source,
                  exclude_source,
                },
              },
            });
          });
        },
      },
    ),
    refetchIntervalInBackground: true,
    ...options,
  });
};

useSearchPdpImagesQuery.getKey = ({
  workspace_id,
  pdp_id,
  image_type,
  limit,
  offset,
  sort_by,
  sort_direction,
  source,
  exclude_source,
}: SearchPdpImagesQueryVariables) => [
  'SearchPdpImages',
  { workspace_id, pdp_id, image_type, limit, offset, sort_by, sort_direction, source, exclude_source },
];

useSearchPdpImagesQuery.fetcher = (
  {
    workspace_id,
    pdp_id,
    image_type,
    limit,
    offset,
    sort_by,
    sort_direction,
    source,
    exclude_source,
  }: SearchPdpImagesQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data, response } = await searchPdpImagesV1WorkspacesWorkspaceIdPdpsPdpIdImagesGet({
      path: { workspace_id, pdp_id },
      query: {
        image_type,
        limit,
        offset,
        sort_by: sort_by as ApiRoutersPortfoliosPdpsPdpsImageViewFields,
        sort_direction,
        source,
        exclude_source,
      },
      throwOnError: true,
    });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};

export type SearchPdpImagesInfiniteQueryVariables = Omit<SearchPdpImagesQueryVariables, 'offset'>;

export const useSearchPdpImagesInfiniteQuery = <
  TData = SearchPdpImagesQuery,
  TError = SearchPdpImagesV1WorkspacesWorkspaceIdPdpsPdpIdImagesGetError,
>(
  {
    workspace_id,
    pdp_id,
    image_type,
    limit,
    sort_by,
    sort_direction,
    source,
    exclude_source,
  }: SearchPdpImagesInfiniteQueryVariables,
  options?: Omit<
    UseInfiniteQueryOptions<SearchPdpImagesQuery, TError, TData>,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  >,
) => {
  return useInfiniteQuery<SearchPdpImagesQuery, TError, TData>({
    queryKey: [
      'SearchPdpImagesInfinite',
      { workspace_id, pdp_id, image_type, limit, sort_by, sort_direction, source, exclude_source },
    ],
    queryFn: async ({ pageParam = 0 }) => {
      const { data, response } = await searchPdpImagesV1WorkspacesWorkspaceIdPdpsPdpIdImagesGet({
        path: { workspace_id, pdp_id },
        query: {
          image_type,
          limit,
          offset: pageParam as number,
          sort_by: sort_by as ApiRoutersPortfoliosPdpsPdpsImageViewFields,
          sort_direction,
          source,
          exclude_source,
        },
        throwOnError: true,
      });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const pageSize = limit || 12; // Default to 12 if no limit specified
      const currentOffset = (allPages.length - 1) * pageSize;
      const nextOffset = currentOffset + pageSize;

      // Check if we have more data to fetch
      if (lastPage.data.length < pageSize || nextOffset >= lastPage.total_count) {
        return undefined; // No more pages
      }

      return nextOffset;
    },
    getPreviousPageParam: (_, allPages) => {
      // Return undefined when we're on the first/only page (initial load)
      // Return a value when we have multiple pages (subsequent loads)
      return allPages.length <= 1 ? undefined : 0;
    },
    initialPageParam: 0,
    refetchInterval: pollWhilePending(
      (query: Query<SearchPdpImagesQuery, TError, InfiniteData<SearchPdpImagesQuery, unknown>>) =>
        getPendingImageTimestamps((query.state.data?.pages ?? []).flatMap((page) => page.data)),
      {
        onTimeout: (pendingCount) => {
          Sentry.withScope((scope) => {
            scope.setFingerprint([
              'GET',
              '/v1/workspaces/{workspace_id}/pdps/{pdp_id}/images',
              'polling_timeout',
              'infinite',
            ]);

            Sentry.captureException(
              new Error('Images still pending after stale window for search pdp images infinite query.'),
              {
                level: 'warning',
                tags: {
                  feature: SENTRY_FEATURE_TAGS.Product,
                  stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Product].productAssetsPolling,
                },
                extra: {
                  workspace_id,
                  pdp_id,
                  pending_count: pendingCount,
                  stale_window_ms: PENDING_STALE_TIMEOUT_MS,
                  query_variables: {
                    workspace_id,
                    pdp_id,
                    image_type,
                    limit,
                    sort_by,
                    sort_direction,
                    source,
                    exclude_source,
                  },
                },
              },
            );
          });
        },
      },
    ),
    refetchIntervalInBackground: true,
    ...options,
  });
};

useSearchPdpImagesInfiniteQuery.getKey = ({
  workspace_id,
  pdp_id,
  image_type,
  limit,
  sort_by,
  sort_direction,
  source,
  exclude_source,
}: SearchPdpImagesInfiniteQueryVariables) => [
  'SearchPdpImagesInfinite',
  { workspace_id, pdp_id, image_type, limit, sort_by, sort_direction, source, exclude_source },
];

useSearchPdpImagesInfiniteQuery.fetcher = (
  {
    workspace_id,
    pdp_id,
    image_type,
    limit,
    sort_by,
    sort_direction,
    source,
    exclude_source,
  }: SearchPdpImagesInfiniteQueryVariables,
  offset = 0,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data, response } = await searchPdpImagesV1WorkspacesWorkspaceIdPdpsPdpIdImagesGet({
      path: { workspace_id, pdp_id },
      query: {
        image_type,
        limit,
        offset,
        sort_by: sort_by as ApiRoutersPortfoliosPdpsPdpsImageViewFields,
        sort_direction,
        source,
        exclude_source,
      },
      throwOnError: true,
    });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};
